import { FEATURE_NAMES } from "@cafe/shared";
import { dt, money, STATUS } from "./api";

/** Понятные подписи вместо внутренних кодов (функции, лимиты, действия журнала, устройства). */

export const featureLabel = (code: string) => FEATURE_NAMES[code] ?? code;

export const PERIOD: Record<string, string> = { month: "Месяц", quarter: "Квартал", year: "Год" };
export const PERIOD_ADV: Record<string, string> = { month: "помесячно", quarter: "поквартально", year: "на год" };

/** Откуда у клиента функция: plan | addon:<код> | override:grant|revoke. */
export function sourceLabel(source: string | null, addonName: (code: string) => string | undefined) {
  if (!source) return null;
  if (source === "plan") return "по тарифу";
  if (source.startsWith("addon:")) return `дополнение «${addonName(source.slice(6)) ?? featureLabel(source.slice(6))}»`;
  if (source === "override:grant") return "включено вручную";
  if (source === "override:revoke") return "отключено вручную";
  return source;
}

export const OVERRIDE_MODE: Record<string, string> = {
  grant: "Включить", revoke: "Отключить", set: "Задать своё значение", delta: "Добавить к тарифу",
};

export const AUDIT_GROUPS = [
  { value: "", label: "Все действия" }, { value: "company.", label: "Компании" }, { value: "subscription.", label: "Подписки" },
  { value: "addon.", label: "Дополнения" }, { value: "invoice.", label: "Счета" }, { value: "plan.", label: "Тарифы" },
  { value: "override.", label: "Ручные настройки функций" }, { value: "impersonation.", label: "Вход в кабинет клиента" },
  { value: "staff.", label: "Сотрудники платформы" }, { value: "cash.", label: "Денежные операции" }, { value: "platform.", label: "Входы в систему" }, { value: "settings.", label: "Настройки" },
];
const AUDIT: Record<string, string> = {
  "company.created": "Создан клиент", "company.updated": "Изменены данные клиента", "company.manager_changed": "Сменён менеджер клиента",
  "subscription.change": "Смена тарифа или периода", "subscription.extend": "Подписка продлена", "subscription.suspend": "Подписка приостановлена",
  "subscription.resume": "Подписка возобновлена", "subscription.cancel": "Подписка отменена",
  "addon.enable": "Дополнение подключено (платно)", "addon.enable_free": "Дополнение подключено бесплатно", "addon.disable": "Дополнение отключено",
  "addon.saved": "Изменено дополнение в каталоге",
  "invoice.created": "Выставлен счёт", "invoice.paid": "Счёт оплачен", "invoice.voided": "Счёт аннулирован",
  "plan.version_created": "Изменён тариф (новая версия)", "plan.migrated": "Клиенты переведены на новую версию тарифа",
  "override.created": "Функция включена/отключена вручную", "override.revoked": "Ручная настройка отменена",
  "impersonation.requested": "Запрос на вход в кабинет клиента", "impersonation.started": "Вход в кабинет клиента", "impersonation.ended": "Выход из кабинета клиента",
  "support.replied": "Ответ на обращение клиента", "support.closed": "Обращение закрыто", "broadcast.sent": "Рассылка клиентам",
  "desktop_release.created": "Загружена версия кассы", "desktop_release.published": "Опубликована версия кассы", "desktop_release.updated": "Изменена версия кассы",
  "desktop_release.withdrawn": "Отозвана версия кассы", "desktop_release.deleted": "Удалена версия кассы",
  "custom_domain.verified": "Свой адрес сайта подтверждён вручную",
  "plan.setup_fees": "Изменена плата за подключение тарифа",
  "cash.created": "Внесена денежная операция", "cash.voided": "Денежная операция отменена",
  "staff.created": "Добавлен сотрудник", "staff.updated": "Изменены данные сотрудника", "staff.deactivated": "Сотрудник отключён", "staff.2fa_reset": "Сброшен код входа сотрудника",
  "platform.login": "Вход в систему", "platform.login_failed": "Неудачный вход (неверный пароль)", "platform.2fa_failed": "Неверный код подтверждения", "platform.password_reset_requested": "Запрошен сброс пароля", "platform.password_reset": "Пароль изменён по ссылке из письма", "platform.telegram_linked": "Подключил Telegram для уведомлений", "platform.telegram_unlinked": "Отключил Telegram", "platform.notify_settings": "Изменил настройки уведомлений", "lead.updated": "Обработал заявку с сайта",
  "settings.updated": "Изменены настройки",
  "menu_pack.updated": "Изменены цена или видимость готового меню", "menu_pack.granted": "Готовое меню подарено клиенту",
};
export const auditLabel = (action: string) => AUDIT[action] ?? action;

export const EVENT: Record<string, string> = {
  plan_changed: "Смена тарифа", addon_enabled: "Дополнение подключено", addon_enabled_free: "Дополнение выдано бесплатно",
  addon_trial: "Дополнение на пробу", addon_disabled: "Дополнение отключено", renewed: "Подписка продлена",
  status_changed: "Изменился статус", invoice_paid: "Счёт оплачен", change_scheduled: "Запланировано изменение",
  menu_pack_paid: "Оплачено готовое меню", invoice_voided: "Счёт аннулирован (заменён новым)",
};

export const DEVICE_TYPE: Record<string, string> = {
  pos: "Касса", kds: "Экран кухни", waiter: "Телефон официанта", kiosk: "Киоск самообслуживания", customer_display: "Экран для гостя",
  ready_screen: "Табло готовности", printer_bridge: "Связь с принтером", desktop: "Программа кассы",
};
export const DEVICE_STATUS: Record<string, string> = { pending: "Ждёт подключения", active: "Работает", revoked: "Отключено" };

const KEYS: Record<string, string> = {
  plan: "тариф", planCode: "тариф", period: "период", version: "версия", reason: "причина", days: "дней", total: "сумма", amount: "сумма",
  number: "счёт", invoice: "счёт", status: "статус", from: "было", to: "стало", mode: "режим", quantity: "количество", until: "до",
  billing: "оплата", periodEnd: "оплачено до", periodStart: "с", trialEnd: "пробный до", featureCode: "функция", value: "значение",
  expiresAt: "до", companies: "компаний", email: "email", role: "роль", name: "название", description: "описание", when: "когда",
  trialDays: "пробных дней", companyName: "компания", ownerName: "владелец", ownerEmail: "email владельца", discountPercent: "скидка, %",
  graceDays: "дней на оплату", readOnlyDays: "дней только просмотра", invoiceLeadDays: "счёт за дней до конца", invoiceDueDays: "дней на оплату счёта",
  bankDetails: "реквизиты", contacts: "контакты", code: "код", method: "способ", reference: "основание", platformNotes: "заметки", codes: "наборы", pack: "набор", prices: "цены", isPublic: "видно клиентам",
};
const VALUES: Record<string, string> = {
  free: "бесплатно", charge: "платно", now: "сразу", period_end: "в конце периода", read_only: "только просмотр", full: "полный доступ",
  bank_transfer: "банковский перевод", renewal: "продление", manual: "вручную", menu_pack: "готовое меню", ...OVERRIDE_MODE,
};
const SKIP = new Set(["invoiceId", "companyId", "id", "addons", "change"]);

function value(k: string, v: unknown): string | null {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return null;
  if (typeof v === "number" && (k === "total" || k === "amount")) return money(v);
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return dt(v);
    if (k === "status" || k === "from" || k === "to") return STATUS[v]?.label ?? v;
    if (k === "period") return PERIOD[v] ?? v;
    if (k === "featureCode" || k === "code") return featureLabel(v);
    return VALUES[v] ?? v;
  }
  if (typeof v === "boolean") return v ? "да" : "нет";
  if (Array.isArray(v)) return v.map(String).join(", ");
  if (typeof v === "object") return describe(v);
  return String(v);
}

/** Объект с данными → «тариф: Бизнес · период: Год · сумма: 4 500 сом». */
export function describe(data: unknown): string {
  if (!data || typeof data !== "object") return data ? String(data) : "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if (k === "change" && v && typeof v === "object") { const s = describe(v); if (s) parts.push(s); continue; }
    if (SKIP.has(k)) continue;
    const s = value(k, v);
    if (s) parts.push(`${KEYS[k] ?? k}: ${s}`);
  }
  return parts.join(" · ");
}

// ───────── финансы платформы ─────────

type Tone = "success" | "warning" | "danger" | "info" | "neutral";

/** Строки оплаченных счетов: из чего сложились продажи. */
export const LINE_KIND: Record<string, string> = {
  plan: "Тарифы", addon: "Дополнения", proration: "Доплаты при смене тарифа и подключении дополнений", setup: "Подключение тарифа", manual: "Разовые счета", discount: "Скидки", menu_pack: "Готовые меню",
};
export const PAY_METHOD: Record<string, string> = { bank_transfer: "Банковский перевод", card: "Карта", qr: "QR-код", manual: "Наличные / вручную" };

/** Состояние клиента в табеле оплат. */
export const PAYMENT_STATE: Record<string, { label: string; tone: Tone; hint: string }> = {
  paid: { label: "Оплачено", tone: "success", hint: "Оплачено, срок не скоро" },
  due_soon: { label: "Скоро платить", tone: "info", hint: "Оплаченный срок кончается в ближайшие 7 дней" },
  awaiting: { label: "Ждём оплату", tone: "warning", hint: "Счёт выставлен, срок оплаты не прошёл" },
  overdue: { label: "Просрочено", tone: "danger", hint: "Срок оплаты счёта прошёл" },
  trial: { label: "Пробный период", tone: "info", hint: "Ещё не платил" },
  blocked: { label: "Заблокирован", tone: "danger", hint: "Только просмотр или приостановлен за неоплату" },
  closed: { label: "Закрыт", tone: "neutral", hint: "Ушёл, пробный кончился без оплаты или в архиве" },
};
/** Клетка табеля: статус счетов месяца. */
export const CELL_STATUS: Record<string, { label: string; tone: Tone }> = {
  paid: { label: "Оплачен", tone: "success" }, open: { label: "Ждём", tone: "warning" }, overdue: { label: "Просрочен", tone: "danger" },
  refunded: { label: "Возврат", tone: "neutral" }, void: { label: "Аннулирован", tone: "neutral" },
};

export const CASH_CATEGORY: Record<string, string> = {
  client_payment: "Оплата клиента", other_income: "Прочий приход", investment: "Вложения",
  salary: "Зарплата", commission: "Вознаграждение менеджеру", marketing: "Реклама", servers: "Серверы и сервисы", rent: "Аренда", taxes: "Налоги", refund: "Возврат денег клиенту", other_expense: "Прочий расход",
};
export const CASH_METHOD: Record<string, string> = { cash: "Наличные", bank: "Банк", card: "Карта" };
